import styled from "@emotion/styled";
import dayjs from 'dayjs';
import { useQuery } from '@apollo/client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFunnelNavigate } from 'src/common/hooks/useFunnelNavigate.hook';
import { useStore } from 'react-redux';
import { CustomerInterface, ProductTypeCodeEnum, RootState, i18n, openCallMeModal, setBListToggle, setCustomer, setFlowType, setGlobalLoading, setOneTimeOffersToggle, setProductTypeCode, setProgressBarPercentage, setSoIdWeb, useAppDispatch, useAppSelector } from 'src/common';
import { setOverflowActive } from 'src/common/redux/state/overflow.slice';
import { Divider, Flex, Loader, OfferInfoCard, OfferV25Card, PartnerBanner, RequestAgentCardV2, SendOffersToEmailCard, Text, VoucherInfoCard } from 'src/components';
import { CancelPolicyInfoCard } from "src/components/cards/cancelPolicyInfo.card";
import { colors } from "src/theme";

import { CalculationResponseDto, getSoPartner, useGetCalculationsQuery } from 'src/common/api/apollo/supplementary';
import { AppRoutes } from '../../_router/app.routes';

// /dopunsko-zdravstveno-osiguranje-b/ponude — v2.5 redesigned offers design. A/B variant (DEV-4667).
// The original baseline is a separate page at /dopunsko-zdravstveno-osiguranje/ponude (offers.page.tsx).

const FilterChip = styled.button<{ selected: boolean }>((props) => ({
  border: 'none',
  cursor: 'pointer',
  borderRadius: 10,
  padding: '10px 16px',
  fontFamily: '"Red Hat Display", sans-serif',
  fontSize: 14,
  fontWeight: 500,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  transition: 'all .15s ease-in-out',
  backgroundColor: props.selected ? colors.background10 : colors.background5,
  color: props.selected ? colors.primary : colors.text2,

  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.5,
  },
}))

const cleanBase64String = (str: string) =>
  // Remove whitespace, new lines, and URL-encoded characters
  str.replace(/\s/g, '').replace(/[^A-Za-z0-9+/=]/g, '');

const fixBase64Padding = (str: string) => {
  while (str.length % 4 !== 0) {
    str += '=';
  }
  return str;
};

const decodeBase64 = (str: string) => {
  const cleanedStr = cleanBase64String(str);
  const fixedStr = fixBase64Padding(cleanedStr);
  try {
    const decodedStr = atob(fixedStr);
    return JSON.parse(decodedStr);
  } catch (e) {
    console.error("Invalid base64 string", e);
    return null;
  }
};

const OffersPageB = () => {
  const location = useLocation();

  const dispatch = useAppDispatch();

  const overflowState = useAppSelector((store: RootState) => store.state.overflow);
  const soIdWeb = useAppSelector((store: RootState) => store.model.offer.soIdWeb);
  const personalVoucherState = useAppSelector((store: RootState) => store.state.personalVoucher);
  const isComparisonFunnel = useAppSelector((store: RootState) => store.state.flow.isComparisonFunnel);

  useEffect(() => {
    dispatch(setProgressBarPercentage(40));
    dispatch(setFlowType('web'));
    dispatch(setProductTypeCode(undefined)); // main flow: clear additional-flow flag
    // Comparison funnel: lock offers to the yearly price so the comparison is apples-to-apples.
    if (isComparisonFunnel) {
      dispatch(setOneTimeOffersToggle(true));
    }
  }, []);

  useEffect(() => {
    // Preuzimanje query parametara
    const params = new URLSearchParams(location.search);
    const base64Param = params.get('param');
    const linkSoId = params.get('soId');

    // Linkovi koji vode ovamo (mail, partner) nose SO s kojim je kupac krenuo — ovaj
    // ulaz ga sam ne kreira. Odvojeno od `param`-a jer link smije nositi samo `soId`.
    // Bez ijednog query parametra ne diramo ništa: soIdWeb je tu postavila forma na
    // prethodnom koraku. Riješeno prije dekodiranja `param`-a da neispravan payload
    // ne preskoči usvajanje SO-a.
    if (linkSoId) {
      dispatch(setSoIdWeb(linkSoId));
    } else if (base64Param) {
      // Link bez soId-a: ono što je ostalo u sessionStorageu može pripadati drugom kupcu.
      dispatch(setSoIdWeb(undefined));
    }

    if (base64Param) {
      // Dekodiranje base64 stringa
      const decoded = decodeBase64(base64Param);

      if (decoded === null) {
        dispatch(setCustomer({ birthDate: undefined, age: undefined, email: undefined, phoneNumber: undefined, countryCode: undefined }));
        return;
      }

      // postavljamo customera samo ako imamo customer podatke, da ne pregazimo postojeće stanje.
      const { birthDate, email, phoneNumber } = decoded;
      if (birthDate || email || phoneNumber) {
        const data: CustomerInterface = {
          birthDate,
          email,
          phoneNumber,
          age: birthDate ? dayjs().diff(dayjs(birthDate), 'year') : undefined
        }
        dispatch(setCustomer(data));
      } else {
        dispatch(setCustomer({ birthDate: undefined, age: undefined, email: undefined, phoneNumber: undefined, countryCode: undefined }));
      }
    }
  }, [dispatch, location.search]);

  // Partner co-branding. Kept out of the offers query on purpose: a failure here must
  // cost the banner, not the whole price list.
  const { data: partnerData } = useQuery(getSoPartner, {
    variables: { soId: soIdWeb ?? '' },
    skip: !soIdWeb,
    fetchPolicy: 'cache-first',
    onError: () => undefined,
  });

  const { bListToggle, oneTimeOffersToggle, editableCoverageSelections } = useAppSelector((store: RootState) => store.model.offer);

  const [bList, setBList] = useState(bListToggle);
  // v2.5 has no one-time toggle in the UI: the value is fixed from redux (and forced
  // to yearly inside the comparison funnel).
  const [oneTime] = useState(isComparisonFunnel ? true : oneTimeOffersToggle);

  const navigate = useFunnelNavigate();
  const lastOffersRef = useRef<typeof data>(undefined);
  const reduxStore = useStore<RootState>();

  const birthDate = useAppSelector((store: RootState) => store.model.customer.data.birthDate) as string;
  const eCard = editableCoverageSelections?.eCard ?? false;

  // comparison funnel (compDZOfunnel): show only offers cheaper than the user's current yearly price
  const insurancePrice = useAppSelector((store: RootState) => store.model.customer.data.insurancePrice);
  const parsedInsurancePrice = Number(String(insurancePrice ?? '').trim().replace(',', '.'));
  const maxPrice = insurancePrice != null && insurancePrice !== '' && !Number.isNaN(parsedInsurancePrice) ? parsedInsurancePrice : undefined;
  const calculationInput = { birthDate, type: ProductTypeCodeEnum.DOPUNSKO, coverages: eCard ? ['eCard'] : [], ...(maxPrice !== undefined ? { maxPrice } : {}) };

  const { loading, error, data, refetch } = useGetCalculationsQuery({
    variables: { input: calculationInput, soId: soIdWeb || '' },
    skip: !birthDate,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    onCompleted: (result: any) => {
      if (result.getCalculations && result.getCalculations.length > 0) {
        if (result.overflowVoucherStatus) {
          dispatch(setOverflowActive({ active: result.overflowVoucherStatus?.active ?? false, voucherCode: result.overflowVoucherStatus?.voucherCode || '', voucherValue: result.overflowVoucherStatus?.voucherValue ?? 0, voucherDescription: result.overflowVoucherStatus?.voucherDescription ?? '' }));
        } else {
          dispatch(setOverflowActive({ active: false, voucherCode: '', voucherValue: 0, voucherDescription: '' }));
        }
      } else {
        dispatch(setOverflowActive({ active: false, voucherCode: '', voucherValue: 0, voucherDescription: '' }));
      }
    }
  });

  const refetchSupplementaryPricing = useCallback(() => {
    if (!birthDate) return;
    refetch({ input: calculationInput, soId: soIdWeb || '' });
  }, [birthDate, refetch, reduxStore]);

  const prevECardRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (!birthDate) return;
    if (prevECardRef.current === undefined) {
      prevECardRef.current = eCard;
      return;
    }
    if (prevECardRef.current !== eCard) {
      prevECardRef.current = eCard;
      dispatch(setGlobalLoading(true));
      Promise.resolve(refetch({ input: calculationInput, soId: soIdWeb || '' }))
        .catch((err) => {
          console.error('Error while refetching offers:', err);
        })
        .finally(() => {
          dispatch(setGlobalLoading(false));
        });
    }
  }, [eCard, birthDate, refetch]);

  if (data) lastOffersRef.current = data;
  const dataToUse = data ?? lastOffersRef.current;

  useEffect(() => {
    if (!birthDate && location.search === '') {
      navigate(AppRoutes.CUSTOMER.fullPath);
    }
  }, [birthDate, location.search, navigate]);

  if (loading && !dataToUse) return <Loader page />;
  if (error) navigate(AppRoutes.CUSTOMER.fullPath);

  // Destrukturiranje podataka
  const { getCalculations: webCalculationsSupplementary} = dataToUse || {};

  if (!webCalculationsSupplementary) return null;

  // Provjera da li treba filtrirati podatke
  const filteredOffers: CalculationResponseDto[] = bList ?
    webCalculationsSupplementary?.filter((offer: any) =>
      offer.fixedCoverages.some((coverage: any) => coverage.code === "drugListB")
    ) :
    webCalculationsSupplementary?.filter((offer: any) =>
      !offer.fixedCoverages.some((coverage: any) => coverage.code === "drugListB")
    )

  // Sortiranje podataka po cijeni
  const sortedOffers = filteredOffers.slice().sort((a: any, b: any) => {
    if (oneTime) {
      return a.prices.generalTransfer - b.prices.generalTransfer;
    }
    return a.prices.generalTransferWithInstallments - b.prices.generalTransferWithInstallments;
  });

  const onBListToggleChange = (checked: boolean) => {
    setBList(checked);
    dispatch(setBListToggle(checked));
  };

  const renderOfferCard = (offer: any) => (
    <OfferV25Card
      key={offer.code}
      offer={offer}
      oneTimeToggle={oneTime}
      bListToggle={bList}
      calculationsLoading={loading}
      onSupplementaryCoverageToggle={refetchSupplementaryPricing}
      isComparisonFunnel={isComparisonFunnel}
    />
  );

  const lowerOffers = isComparisonFunnel ? sortedOffers.filter((o: any) => (o.diffPrice ?? 0) >= 0) : [];
  const higherOffers = isComparisonFunnel ? sortedOffers.filter((o: any) => (o.diffPrice ?? 0) < 0) : [];

  return (
    <Flex vertical gap={24}>
      <PartnerBanner code={partnerData?.soPartner?.code} name={partnerData?.soPartner?.name} />
      <CancelPolicyInfoCard />
      <VoucherInfoCard active={overflowState.overflowActive} voucherCode={overflowState.voucherCode} text={overflowState.voucherDescription} />
      {!overflowState.overflowActive && personalVoucherState.personalVoucher?.status === 'Active' && personalVoucherState.personalVoucher?.value && personalVoucherState.personalVoucher.value > 0 && (
        <VoucherInfoCard active={true} voucherCode={personalVoucherState.personalVoucher?.code ?? ''} text={personalVoucherState.personalVoucher?.description ?? ''}
          showCode={true}
          endDate={personalVoucherState.personalVoucher?.endDate ?? ''}
        />
      )}
      <Text type="xLarge" bold>{i18n.t('pages.offersPage.title')}</Text>
      <Flex vertical gap={12}>
        <Text type="base" color={colors.text2}>{i18n.t('pages.offersPage.filterHeading')}</Text>
        <Flex gap={8} justify="center" wrap="nowrap">
          <FilterChip selected={!!bList} disabled={isComparisonFunnel} onClick={() => onBListToggleChange(!bList)}>
            {i18n.t('pages.offersPage.bListChip')}
          </FilterChip>
        </Flex>
      </Flex>
      <Divider dashed />
      {isComparisonFunnel ? (
        <>
          {lowerOffers.map(renderOfferCard)}
          {lowerOffers.length > 0 && higherOffers.length > 0 && (
            <Flex align="center" vertical gap={4}>
              <Text type="medium" bold>SKUPLJE OPCIJE OSIGURANJA</Text>
              <Divider dashed />
              <Text type="small">Uključuju jednaka pokrića</Text>
            </Flex>
          )}
          {higherOffers.map(renderOfferCard)}
        </>
      ) : (
        sortedOffers?.map(renderOfferCard)
      )}
      {false && !bList && <OfferInfoCard priceOneTime={180} priceInstallments={15} oneTimeToggle={oneTime} />}
      <SendOffersToEmailCard />
      <RequestAgentCardV2 onButtonClick={() => dispatch(openCallMeModal())} />
    </Flex>
  );
}

export default OffersPageB;
